/**
 * Scope / Ownership checks — autorización a nivel de fila/entidad.
 *
 * El motor `can()` (matriz + PERMS) responde "este rol tiene permiso para
 * esta acción en general?". Estas funciones responden la pregunta posterior:
 * "este usuario tiene permiso para esta acción **sobre esta entidad concreta**?".
 *
 * Reglas (VD §3.2 - §3.4):
 *
 *   - Roles "globales" (JEFE, GTE, ADMIN_PMIS) tienen acceso completo a
 *     todas las entidades del PMIS.
 *
 *   - **AFE**: sólo el PL titular (`afe.plId`) o el Back-PL (`afe.backPlId`)
 *     pueden proponer cambios. CFE y PL2 ven todos los AFEs (auditoría) pero
 *     no editan los que no son suyos.
 *
 *   - **PR / PO**: cualquier PL/PL2/CFE las crea para cualquier AFE. Tras
 *     crearlas heredan permiso completo:
 *       (a) el PL titular y el Back-PL del AFE al que pertenecen — el cambio
 *           de PL en el AFE re-asigna automáticamente la autoridad sobre las
 *           PR/PO derivadas;
 *       (b) el usuario creador (`createdBy`);
 *       (c) cualquier PL/PL2/CFE de la MISMA disciplina que el creador
 *           (back-up natural por disciplina).
 *     ABAS gestiona en sus estados de compra. Roles globales editan todas.
 *
 *   - **SO**: autoridad **del PL2 que la solicitó**, NO del PL del AFE.
 *     Operativamente: el PL2 es quien hace la edición y seguimiento; el PL
 *     del AFE solo interviene a solicitud del PL2. Por eso la SO no hereda
 *     automáticamente la autoridad desde el AFE. La autoridad se compone de:
 *       (a) el creador de la SO (`so.createdBy` = email del PL2 solicitante);
 *       (b) el **back-PL2**: cualquier PL/PL2/CFE con la MISMA disciplina
 *           que el PL2 solicitante (resuelta vía `usuarios`). Aquí entra,
 *           p.ej., Eduardo Zambrano (MECÁNICA) cuando el PL2 también es
 *           MECÁNICA, sin importar la disciplina del contrato.
 *       (c) PL/PL2/CFE de la disciplina del contrato origen (Contrato Marco
 *           o PR de la PO puntual) — back-up secundario para casos donde
 *           el PL2 y el contrato no comparten disciplina.
 *     Roles globales (JEFE/GTE/ADMIN_PMIS) gestionan todas.
 *
 *   - **Contrato Marco**: PLs/PL2s cuya `disciplina` coincide con la del
 *     contrato pueden proponer cambios. El JEFE aprueba.
 *
 *   - **Contratistas (PLANNER/ADOR)**: sólo ven SOs/RDs/Actas/Eventos
 *     vinculados a su empresa (`contratistaId` o `contratistaNombre` del SO
 *     coincide con su perfil). Nunca acceden a AFE/Budget/PR/PO/Contratos.
 *
 *   - **Interventor (INT)**: sólo ve SOs/RDs/Actas/Eventos donde fue
 *     asignado (`so.interventorId === user.email`). Nunca accede a
 *     AFE/Budget/PR/PO/Contratos.
 */

import type { Afe, ContratoMarco, Pr, Po, So, Usuario } from "@app-types/entities";
import type { Role } from "@constants/roles";
import { mismaEmpresa } from "@/lib/empresa";

type UsuarioRef = Pick<Usuario, "email" | "disciplina">;
type UsuarioInt = Pick<Usuario, "email" | "rol" | "disciplina" | "empresa">;

/** Roles que ven y editan todo (auditores, administración superior). */
const ROLES_GLOBALES = new Set<Role>(["JEFE", "GTE", "ADMIN_PMIS"]);

/** Roles considerados "contratista" del lado del proveedor. */
const ROLES_CONTRATISTA = new Set<Role>(["PLANNER", "ADOR"]);

export interface UsuarioContexto {
  email: string;
  role: Role | null;
  /** Disciplina del usuario (PL/PL2/INT/PLANNER). Para scope de contratos marco. */
  disciplina?: Usuario["disciplina"];
  /** ID del contratista al que pertenece (PLANNER/ADOR). */
  contratistaId?: string;
  /** Razón social del contratista (heurística secundaria por nombre). */
  empresa?: string;
}

export function esRolGlobal(role: Role | null): boolean {
  return role !== null && ROLES_GLOBALES.has(role);
}

export function esContratista(role: Role | null): boolean {
  return role !== null && ROLES_CONTRATISTA.has(role);
}

export function esInterventor(role: Role | null): boolean {
  return role === "INT";
}

// ============================================================================
// AFE — sólo PL/Back-PL del AFE + roles globales
// ============================================================================

/**
 * El usuario puede VER el AFE (lista o detalle). Los contratistas e INT no ven
 * AFEs en absoluto; el rol Tecpetrol los ve todos.
 */
export function puedeVerAfe(_afe: Pick<Afe, "plId" | "backPlId">, user: UsuarioContexto): boolean {
  if (!user.role) return false;
  if (esContratista(user.role) || esInterventor(user.role)) return false;
  return true;
}

/**
 * El usuario puede proponer/editar cambios al AFE.
 * Solo PL titular, Back-PL, JEFE, GTE y ADMIN_PMIS.
 */
export function puedeEditarAfe(
  afe: Pick<Afe, "plId" | "backPlId">,
  user: UsuarioContexto,
): boolean {
  if (!user.role) return false;
  if (esRolGlobal(user.role)) return true;
  return afe.plId === user.email || afe.backPlId === user.email;
}

/**
 * El usuario puede PROPONER un cambio del valor del AFE. Solo el PL titular o el
 * Back-PL del AFE — nadie más (ni PL2, ni CFE, ni roles globales, que son los
 * aprobadores). El valor oficial no cambia hasta que un JEFE/GTE aprueba.
 */
export function puedeProponerCambioAfe(
  afe: Pick<Afe, "plId" | "backPlId">,
  user: UsuarioContexto,
): boolean {
  if (!user.role) return false;
  return afe.plId === user.email || afe.backPlId === user.email;
}

/**
 * El usuario puede APROBAR o RECHAZAR una propuesta de cambio de valor del AFE.
 * Solo JEFE o GTE (los aprobadores). Recién con su aprobación el valor queda en
 * firme.
 */
export function puedeAprobarCambioAfe(user: UsuarioContexto): boolean {
  return user.role === "JEFE" || user.role === "GTE";
}

// ============================================================================
// PR / PO — scope por creador + back-up natural por disciplina
// ============================================================================

const ROLES_DISCIPLINA = new Set<Role>(["PL", "PL2", "CFE"]);

/**
 * Reglas de quién puede CREAR una PR según su disciplina:
 *
 *   - `INTERVENTORIA`: sólo CFE (más JEFE/GTE/ADMIN_PMIS por ser globales).
 *     Los PL/PL2 no crean PRs de interventoría porque ese gasto es
 *     administrativo y lo controlan controlling (CFE) y gerencia (JEFE).
 *
 *   - Resto de disciplinas (`GENERAL`, `CIVIL`, `MECANICA`, `ELECTRICA`,
 *     `INSTRUMENTACION`): cualquier PL/PL2/CFE puede crear, sin restricción
 *     por disciplina del usuario.
 */
export function puedeCrearPrPorDisciplina(
  disciplina: string | undefined,
  user: UsuarioContexto,
): { puede: boolean; motivo?: string } {
  if (!user.role) return { puede: false, motivo: "Sin rol asignado." };
  if (esRolGlobal(user.role)) return { puede: true };
  if (disciplina === "INTERVENTORIA") {
    if (user.role === "CFE") return { puede: true };
    return {
      puede: false,
      motivo:
        "PRs de disciplina Interventoría sólo pueden crearlas el JEFE y el CFE.",
    };
  }
  if (ROLES_DISCIPLINA.has(user.role)) return { puede: true };
  return { puede: false, motivo: `El rol ${user.role} no puede crear PRs.` };
}

function comparteDisciplinaConCreador(
  creadorEmail: string,
  usuarios: UsuarioRef[],
  user: UsuarioContexto,
): boolean {
  if (!user.role || !ROLES_DISCIPLINA.has(user.role) || !user.disciplina) return false;
  const creador = usuarios.find((u) => u.email === creadorEmail);
  if (!creador || !creador.disciplina) return false;
  return creador.disciplina === user.disciplina;
}

/**
 * `true` si el usuario es el PL titular o el Back-PL del AFE indicado.
 * Cuando un AFE se reasigna a otro PL/back-PL, esta función refleja el cambio
 * inmediatamente — es la vía por la que los PR/PO/SO derivados heredan
 * autoridad desde su AFE.
 */
function esPlOBackPlDelAfe(
  afeId: string,
  afes: Pick<Afe, "id" | "plId" | "backPlId">[],
  user: UsuarioContexto,
): boolean {
  const afe = afes.find((a) => a.id === afeId);
  if (!afe) return false;
  return afe.plId === user.email || afe.backPlId === user.email;
}

export function puedeEditarPr(
  pr: Pick<Pr, "createdBy" | "afeId">,
  afes: Pick<Afe, "id" | "plId" | "backPlId">[],
  usuarios: UsuarioRef[],
  user: UsuarioContexto,
): boolean {
  if (!user.role) return false;
  if (esRolGlobal(user.role)) return true;
  // ABAS gestiona las PRs en sus estados de compra (ET-FAC, EE-ABAS).
  if (user.role === "ABAS") return true;
  // PL/Back-PL del AFE — autoridad canónica heredada desde el AFE.
  if (esPlOBackPlDelAfe(pr.afeId, afes, user)) return true;
  // Creador siempre puede editar lo suyo.
  if (pr.createdBy === user.email) return true;
  // Back-up natural: PL/PL2/CFE de la misma disciplina que el creador.
  return comparteDisciplinaConCreador(pr.createdBy, usuarios, user);
}

/**
 * Scope de la PO. Se considera ownership del equipo de la PR origen:
 * cuando la PO viene de una PR (`po.prId`), el creador de la PR y los
 * back-ups naturales de su misma disciplina (PL/PL2/CFE) gestionan
 * revisiones, aprobaciones y la creación misma de la PO.
 *
 * Además mantiene el ownership del creador de la PO (para POs standalone
 * sin PR origen) y su back-up por disciplina.
 *
 * ABAS y roles globales editan todas.
 */
export function puedeEditarPo(
  po: Pick<Po, "createdBy" | "prId" | "afeId">,
  prs: Pick<Pr, "id" | "createdBy">[],
  afes: Pick<Afe, "id" | "plId" | "backPlId">[],
  usuarios: UsuarioRef[],
  user: UsuarioContexto,
): boolean {
  if (!user.role) return false;
  if (esRolGlobal(user.role)) return true;
  if (user.role === "ABAS") return true;
  // 1) PL/Back-PL del AFE de la PO — autoridad canónica heredada del AFE.
  if (esPlOBackPlDelAfe(po.afeId, afes, user)) return true;
  // 2) Creador de la PO o su back-up por disciplina
  if (po.createdBy === user.email) return true;
  if (comparteDisciplinaConCreador(po.createdBy, usuarios, user)) return true;
  // 3) Equipo de la PR origen (creador de la PR + back-ups por disciplina)
  if (po.prId) {
    const pr = prs.find((p) => p.id === po.prId);
    if (pr) {
      if (pr.createdBy === user.email) return true;
      if (comparteDisciplinaConCreador(pr.createdBy, usuarios, user)) return true;
    }
  }
  return false;
}

/**
 * Verifica si el usuario puede CREAR una PO a partir de la PR seleccionada.
 *
 * Regla: la PO la inician el creador de la PR o sus back-ups naturales
 * (PL/PL2/CFE de la misma disciplina). ABAS y roles globales también.
 *
 * Si la PO se crea sin PR origen (standalone), cualquier PL/PL2/CFE puede.
 */
export function puedeCrearPoDesdePr(
  pr: Pick<Pr, "createdBy"> | null | undefined,
  usuarios: UsuarioRef[],
  user: UsuarioContexto,
): { puede: boolean; motivo?: string } {
  if (!user.role) return { puede: false, motivo: "Sin rol asignado." };
  if (esRolGlobal(user.role)) return { puede: true };
  if (user.role === "ABAS") return { puede: true };

  // PO standalone (sin PR origen): cualquier PL/PL2/CFE
  if (!pr) {
    if (ROLES_DISCIPLINA.has(user.role)) return { puede: true };
    return { puede: false, motivo: `El rol ${user.role} no puede crear POs.` };
  }
  // PO con PR origen: sólo el creador de la PR o su back-up por disciplina
  if (pr.createdBy === user.email) return { puede: true };
  if (comparteDisciplinaConCreador(pr.createdBy, usuarios, user)) {
    return { puede: true };
  }
  return {
    puede: false,
    motivo:
      "Sólo el creador de la PR origen y sus back-ups por disciplina (PL/PL2/CFE) pueden crear POs a partir de esta PR.",
  };
}

// ============================================================================
// SO — scope múltiple: PL del AFE, contratista de la SO, interventor asignado
// ============================================================================

/**
 * El usuario puede VER la SO en su listado.
 *
 *   - Tecpetrol (PL/PL2/CFE/JEFE/GTE/ABAS/ADMIN_PMIS): ven todas.
 *   - Contratista (PLANNER/ADOR): sólo las de su empresa.
 *   - Interventor (INT): sólo aquellas donde fue asignado.
 */
/**
 * El usuario es el Interventor asignado a la SO, o su BACK natural: otro INT
 * de la **misma disciplina** y **misma empresa interventora**. Así el
 * interventor suplente accede y gestiona los RD de su colega.
 *
 * Ej.: si la SO la lleva Luis Hernández (Eléctrica), Giovanni Sarmiento
 * (Eléctrica, misma interventoría) es su back y tiene el mismo acceso.
 */
export function esInterventorOBackDeLaSo(
  so: { interventorId: string },
  usuarios: UsuarioInt[],
  user: UsuarioContexto,
): boolean {
  if (user.role !== "INT") return false;
  if (so.interventorId === user.email) return true;
  const titular = usuarios.find((u) => u.email === so.interventorId);
  if (!titular || titular.rol !== "INT" || !titular.disciplina) return false;
  return (
    !!user.disciplina &&
    user.disciplina === titular.disciplina &&
    mismaEmpresa(user.empresa, titular.empresa)
  );
}

/**
 * Correos de quienes ejercen la interventoría de una OT: el titular y sus backs
 * naturales (misma disciplina y misma empresa interventora).
 *
 * Es el reverso de `esInterventorOBackDeLaSo`: aquélla responde "¿este usuario
 * puede?", ésta responde "¿a quiénes hay que avisarles?". Las notificaciones
 * deben usar esta lista para no dejar por fuera al back, que sí está autorizado.
 */
export function interventoresDeLaSo(
  so: { interventorId: string },
  usuarios: UsuarioInt[],
): string[] {
  if (!so.interventorId) return [];
  const titular = usuarios.find((u) => u.email === so.interventorId);
  const emails = [so.interventorId];
  if (titular?.rol === "INT" && titular.disciplina) {
    for (const u of usuarios) {
      if (u.email === so.interventorId) continue;
      if (u.rol !== "INT" || u.disciplina !== titular.disciplina) continue;
      if (!mismaEmpresa(u.empresa, titular.empresa)) continue;
      emails.push(u.email);
    }
  }
  return emails;
}

export function puedeVerSo(
  so: Pick<
    So,
    "contratistaId" | "contratistaNombre" | "interventorId" | "pendienteVoBoDisciplina"
  >,
  user: UsuarioContexto,
  usuarios: UsuarioInt[] = [],
): boolean {
  if (!user.role) return false;
  if (esRolGlobal(user.role)) return true;

  if (esContratista(user.role)) {
    // Una OT retenida a la espera del visto bueno de disciplina NO existe para
    // el contratista: no la ve en la lista, ni por enlace directo, ni recibe
    // aviso. Es la compuerta única — cerrarla aquí cierra todas las superficies
    // que pasan por `puedeVerSo`.
    if (so.pendienteVoBoDisciplina) return false;
    // Una sola llave: empresa del usuario (Personal) === contratistaNombre.
    return mismaEmpresa(user.empresa, so.contratistaNombre);
  }

  if (esInterventor(user.role)) {
    // El interventor asignado y su back natural por disciplina.
    return esInterventorOBackDeLaSo(so, usuarios, user);
  }

  // Resto: PL/PL2/CFE/ABAS — ven todas (auditoría); editan según scope del AFE.
  return true;
}

/**
 * Resuelve la disciplina aplicable a una SO según su contrato origen:
 *   - SO sobre Contrato Marco (`so.contratoMarcoId`) → `cm.disciplina`.
 *   - SO de servicio puntual (`so.poId`) → disciplina de la PR origen del
 *     PO. Las POs no llevan disciplina propia; la heredan de su PR.
 */
function disciplinaDeOrigenSo(
  so: Pick<So, "contratoMarcoId" | "poId">,
  contratos: Pick<ContratoMarco, "id" | "disciplina">[],
  pos: Pick<Po, "id" | "prId">[],
  prs: Pick<Pr, "id" | "disciplina">[],
): string | undefined {
  if (so.contratoMarcoId) {
    return contratos.find((c) => c.id === so.contratoMarcoId)?.disciplina;
  }
  if (so.poId) {
    const po = pos.find((p) => p.id === so.poId);
    if (!po?.prId) return undefined;
    return prs.find((p) => p.id === po.prId)?.disciplina;
  }
  return undefined;
}

/**
 * El usuario puede gestionar (proponer cambios, revisar, aprobar) la SO.
 *
 * Regla (canónica) — modelo "la SO la lleva el PL2 que la solicitó":
 *   1. Roles globales (JEFE/GTE/ADMIN_PMIS): siempre.
 *   2. El PL2 que solicitó la SO (`so.createdBy === user.email`): siempre.
 *   3. **Back-PL2**: PL/PL2/CFE con la misma disciplina que el PL2
 *      solicitante (resuelta vía `usuarios`).
 *   4. PL/PL2/CFE con la disciplina del contrato origen (Contrato Marco o
 *      PR de la PO puntual) — back-up secundario.
 *
 * **No** se hereda autoridad desde el PL/Back-PL del AFE: ellos gestionan
 * el AFE y las PR/PO derivadas, pero sobre la SO solo actúan a solicitud
 * del PL2.
 *
 * Los contratistas (PLANNER/ADOR) **NO** entran por esta vía — su
 * acción específica de "Enviar cotización" se autoriza directamente en la
 * UI tras pasar el filtro `puedeVerSo` que ya restringe a sus propias SOs.
 */
/**
 * Verifica si el usuario puede CREAR una OT sobre un Contrato Marco.
 *
 * Por flexibilidad y colaboración, **cualquier** PL/PL2/CFE puede crear una OT
 * sobre un contrato de **cualquier disciplina**. El control disciplinar no se
 * pierde: se traslada a la LIBERACIÓN — la OT creada por alguien de otra
 * disciplina queda retenida hasta que un PL2 de la disciplina del contrato le dé
 * el visto bueno (ver `requiereVoBoDisciplina` y `puedeDarVoBoDisciplina`).
 */
export function puedeCrearSoEnContratoMarco(
  cm: Pick<ContratoMarco, "disciplina"> | null | undefined,
  user: UsuarioContexto,
): { puede: boolean; motivo?: string } {
  if (!user.role) return { puede: false, motivo: "Sin rol asignado." };
  if (esRolGlobal(user.role)) return { puede: true };
  if (!ROLES_DISCIPLINA.has(user.role)) {
    return {
      puede: false,
      motivo: `El rol ${user.role} no puede crear SOs.`,
    };
  }
  if (!cm) return { puede: false, motivo: "Contrato Marco no encontrado." };
  // CM sin disciplina: compatibilidad — no se bloquea.
  if (!cm.disciplina) return { puede: true };
  if (!user.disciplina) {
    return {
      puede: false,
      motivo:
        "Tu usuario no tiene disciplina asignada. Pide al administrador que la fije antes de crear OTs.",
    };
  }
  // Disciplina distinta: SE PERMITE crear. La OT nacerá retenida y no llegará al
  // contratista hasta el visto bueno de un PL2 de la disciplina del contrato.
  return { puede: true };
}

export function puedeEditarSo(
  so: Pick<So, "contratoMarcoId" | "poId" | "createdBy">,
  contratos: Pick<ContratoMarco, "id" | "disciplina">[],
  pos: Pick<Po, "id" | "prId">[],
  prs: Pick<Pr, "id" | "disciplina">[],
  usuarios: UsuarioRef[],
  user: UsuarioContexto,
): boolean {
  if (!user.role) return false;
  if (esRolGlobal(user.role)) return true;
  // (2) Solicitante directo: el PL2 que creó la SO.
  if (so.createdBy === user.email) return true;
  // A partir de aquí solo PL/PL2/CFE con disciplina propia siguen jugando.
  if (!ROLES_DISCIPLINA.has(user.role) || !user.disciplina) return false;
  // (3) Back-PL2: misma disciplina que el PL2 solicitante.
  if (comparteDisciplinaConCreador(so.createdBy, usuarios, user)) return true;
  // (4) Back-up secundario: misma disciplina que el contrato origen.
  const disc = disciplinaDeOrigenSo(so, contratos, pos, prs);
  if (!disc) return false;
  return user.disciplina === disc;
}

// ============================================================================
// Actas — scope por contratista al que pertenece la SO del acta
// ============================================================================

/**
 * El ADOR/PLANNER puede firmar el "lado contratista" de un acta solo si
 * pertenece a la **misma empresa** que la SO del acta. Sin esta verificación,
 * un ADOR de Soforesta podría firmar un acta de Metálicas Brayan.
 *
 * Roles globales (JEFE/GTE/ADMIN_PMIS) siempre pueden (auditoría).
 */
export function puedeFirmarActaContratista(
  acta: { soId: string },
  sos: Pick<So, "id" | "contratistaNombre">[],
  user: UsuarioContexto,
): boolean {
  if (!user.role) return false;
  if (esRolGlobal(user.role)) return true;
  if (user.role !== "ADOR" && user.role !== "PLANNER") return false;
  const so = sos.find((s) => s.id === acta.soId);
  if (!so) return false;
  return mismaEmpresa(user.empresa, so.contratistaNombre);
}

/**
 * ¿El usuario pertenece a la empresa contratista dueña de la OT?
 *
 * Doble llave, el mismo criterio que ya usan `RdListPage` y
 * `SoCotizacionPage`: `contratistaId` es la llave robusta y el nombre de
 * empresa el respaldo para los registros que todavía no lo traen.
 * `mismaEmpresa` por sí sola no basta: devuelve false con empresa vacía y no
 * tolera "SAS" frente a "S.A.S".
 */
export function perteneceAlContratistaDeLaSo(
  so: Pick<So, "contratistaId" | "contratistaNombre">,
  user: UsuarioContexto,
): boolean {
  if (user.contratistaId != null && so.contratistaId === user.contratistaId) return true;
  return mismaEmpresa(user.empresa, so.contratistaNombre);
}

/**
 * ¿Puede este usuario GENERAR un acta sobre esta OT?
 *
 * Hasta ahora la única compuerta era `can("act:generate")`, un permiso binario
 * por rol: cualquier PLANNER/ADOR podía generar actas sobre la OT de un
 * competidor. El listado de actas sí filtraba con `puedeVerSo`, pero el
 * selector del generador recibía la lista cruda de OTs del tenant.
 *
 * Se filtra además por estado: solo hay obra que certificar sobre una OT en
 * ejecución o ya cerrada. Una OT en cotización, cancelada, o retenida a la
 * espera del visto bueno de disciplina, no debe aparecer en el selector.
 */
export function puedeGenerarActaDeSo(
  so: Pick<
    So,
    "estado" | "contratistaId" | "contratistaNombre" | "pendienteVoBoDisciplina"
  >,
  user: UsuarioContexto,
): boolean {
  if (!user.role) return false;
  if (so.estado !== "APROBADA" && so.estado !== "CERRADA") return false;
  if (so.pendienteVoBoDisciplina) return false;
  if (esRolGlobal(user.role)) return true;
  if (esContratista(user.role)) return perteneceAlContratistaDeLaSo(so, user);
  // PL/PL2/CFE certifican sobre cualquier OT — suplencia y auditoría.
  return true;
}

/**
 * ¿Puede este usuario ABRIR el acta de esta OT?
 *
 * La ruta `/actas/:id` no tenía ningún guard: con el id, un contratista abría
 * el acta completa de otro — cantidades, precios unitarios, desglose AIU y
 * firmas.
 *
 * Deliberadamente NO se reutiliza `puedeVerSo`. Para el INT, esa función exige
 * ser el interventor titular o su back por disciplina, condición que hoy NO se
 * exige para firmar: aplicarla aquí dejaría a un interventor suplente sin
 * poder abrir el acta, y el documento atascado sin ninguna pantalla que
 * reasigne interventor. Y para el contratista aplica la compuerta
 * `pendienteVoBoDisciplina`, que gobierna la visibilidad de la OT, no la de un
 * acta ya emitida.
 */
export function puedeVerActaDeSo(
  so: Pick<So, "contratistaId" | "contratistaNombre">,
  user: UsuarioContexto,
): boolean {
  if (!user.role) return false;
  if (esRolGlobal(user.role)) return true;
  if (esContratista(user.role)) return perteneceAlContratistaDeLaSo(so, user);
  // INT y personal Tecpetrol: mismo alcance que hoy.
  return true;
}

// ============================================================================
// Reportes Diarios — scope por interventor asignado a la SO
// ============================================================================

/**
 * El INT puede aprobar un RD solo si es **el interventor asignado** a la SO
 * del RD o su **back natural** (otro INT de la misma disciplina e
 * interventoría). Sin esto, cualquier INT con `rd:approve_int` podría
 * aprobar cualquier RD.
 *
 * Roles globales (JEFE/GTE/ADMIN_PMIS) siempre pueden (auditoría).
 */
export function puedeAprobarRdInt(
  rd: { soId: string },
  sos: Pick<So, "id" | "interventorId">[],
  usuarios: UsuarioInt[],
  user: UsuarioContexto,
): boolean {
  if (!user.role) return false;
  if (esRolGlobal(user.role)) return true;
  if (user.role !== "INT") return false;
  const so = sos.find((s) => s.id === rd.soId);
  if (!so) return false;
  return esInterventorOBackDeLaSo(so, usuarios, user);
}

// ============================================================================
// Contrato Marco — scope por disciplina del PL/PL2
// ============================================================================

export function puedeVerContrato(_cm: ContratoMarco, user: UsuarioContexto): boolean {
  if (!user.role) return false;
  // Contratistas e INT: nunca ven contratos marco.
  if (esContratista(user.role) || esInterventor(user.role)) return false;
  return true;
}

/**
 * Sólo los PL/PL2 cuya disciplina coincide con la del contrato marco pueden
 * proponer cambios. Roles globales (JEFE/GTE/ADMIN_PMIS) y CFE pueden todo.
 */
export function puedeEditarContrato(cm: Pick<ContratoMarco, "disciplina">, user: UsuarioContexto): boolean {
  if (!user.role) return false;
  if (esRolGlobal(user.role)) return true;
  if (user.role === "CFE") return true;
  if (user.role !== "PL" && user.role !== "PL2") return false;
  // Si el contrato no declara disciplina, cualquier PL/PL2 puede proponer
  // (compatibilidad con seeds viejos).
  if (!cm.disciplina) return true;
  return user.disciplina === cm.disciplina;
}

// ============================================================================
// Usuarios / Autoridades
// ============================================================================

/**
 * El usuario actor puede editar el registro de otro usuario.
 *
 * Reglas:
 *   - **ADMIN_PMIS / GTE / JEFE**: editan a todos.
 *   - **PL / PL2 / CFE** (con disciplina asignada): editan únicamente a
 *     usuarios vinculados a contratos marco de su misma disciplina. El
 *     vínculo se valida por `targetUser.contratistaId` contra los CMs de
 *     esa disciplina; si el usuario destino tiene una `disciplina` propia
 *     (caso INT/PLANNER/etc. con disciplina explícita), también basta con
 *     que coincida con la del actor.
 *   - Los demás roles no editan a otros usuarios.
 */
export function puedeEditarUsuario(
  targetUser: Pick<Usuario, "disciplina" | "contratistaId">,
  contratos: Pick<ContratoMarco, "disciplina" | "contratistaId">[],
  user: UsuarioContexto,
): boolean {
  if (!user.role) return false;
  // Globales: ADMIN_PMIS, JEFE, GTE
  if (esRolGlobal(user.role)) return true;
  // PL/PL2/CFE actor: necesita disciplina propia para hacer match
  if (!ROLES_DISCIPLINA.has(user.role) || !user.disciplina) return false;
  // Coincidencia directa por disciplina del usuario destino
  if (targetUser.disciplina && targetUser.disciplina === user.disciplina) {
    return true;
  }
  // Coincidencia indirecta por contratista: si el usuario destino pertenece
  // a un contratista que tiene CMs de la disciplina del actor.
  if (targetUser.contratistaId) {
    return contratos.some(
      (c) =>
        c.contratistaId === targetUser.contratistaId &&
        c.disciplina === user.disciplina,
    );
  }
  return false;
}

// ============================================================================
// Visto bueno de disciplina sobre una OT creada por otra disciplina
// ============================================================================

/**
 * ¿La OT que se va a crear queda RETENIDA a la espera del visto bueno de la
 * disciplina del contrato?
 *
 * Sí cuando quien la crea no es de la disciplina del contrato marco. Los roles
 * globales y los contratos sin disciplina no retienen.
 */
export function requiereVoBoDisciplina(
  cm: Pick<ContratoMarco, "disciplina"> | null | undefined,
  user: UsuarioContexto,
): boolean {
  if (!user.role || esRolGlobal(user.role)) return false;
  if (!cm?.disciplina) return false;
  return user.disciplina !== cm.disciplina;
}

/**
 * ¿El usuario puede dar el visto bueno que libera la OT hacia el contratista?
 *
 * Cadena de autoridad definida por el responsable del PMIS:
 *   1. Cualquier PL/PL2/CFE cuya disciplina coincida con la del contrato.
 *   2. Si NO existe ninguno en el sistema, la suplencia recae en quien creó el
 *      contrato marco y en su back — que son los demás PL/PL2/CFE de la
 *      disciplina de ese creador.
 *
 * El paso 2 no es circular: usa la disciplina del CREADOR del contrato, no la
 * del contrato, que por definición no tiene responsable.
 *
 * Caso real que lo hace necesario: no hay ningún PL2 en las disciplinas
 * INTERVENTORIA ni GENERAL, y existen contratos marco de esas disciplinas.
 */
export function puedeDarVoBoDisciplina(
  cm: Pick<ContratoMarco, "disciplina" | "createdBy"> | null | undefined,
  usuarios: UsuarioInt[],
  user: UsuarioContexto,
): boolean {
  if (!user.role) return false;
  if (esRolGlobal(user.role)) return true;
  if (!ROLES_DISCIPLINA.has(user.role)) return false;
  if (!cm) return false;
  // 1) Titulares: los de la disciplina del contrato.
  if (cm.disciplina && user.disciplina === cm.disciplina) return true;
  // ¿Existe algún titular en el sistema? Si lo hay, nadie más libera.
  const hayTitular =
    !!cm.disciplina &&
    usuarios.some(
      (u) => ROLES_DISCIPLINA.has(u.rol) && u.disciplina === cm.disciplina,
    );
  if (hayTitular) return false;
  // 2) Suplencia: el creador del contrato marco y su back por disciplina.
  if (!cm.createdBy) return false;
  if (cm.createdBy === user.email) return true;
  const creador = usuarios.find((u) => u.email === cm.createdBy);
  if (!creador?.disciplina) return false;
  return user.disciplina === creador.disciplina;
}

/**
 * Correos de quienes pueden dar el visto bueno que libera una OT retenida.
 *
 * Es el reverso de `puedeDarVoBoDisciplina` — aquélla responde "¿este usuario
 * puede?", ésta "¿a quiénes hay que avisarles?" — y sigue la misma cadena:
 * titulares de la disciplina del contrato y, si no existe ninguno, el creador
 * del contrato marco con su back. Sin esta lista la OT retenida no tendría cola
 * y podría quedarse invisible indefinidamente.
 */
export function aprobadoresVoBoDisciplina(
  cm: Pick<ContratoMarco, "disciplina" | "createdBy"> | null | undefined,
  usuarios: UsuarioInt[],
): string[] {
  if (!cm) return [];
  const esAutoridad = (u: UsuarioInt) => ROLES_DISCIPLINA.has(u.rol);
  if (cm.disciplina) {
    const titulares = usuarios
      .filter((u) => esAutoridad(u) && u.disciplina === cm.disciplina)
      .map((u) => u.email);
    if (titulares.length > 0) return titulares;
  }
  // Suplencia: el creador del contrato marco y su back por disciplina.
  // Solo se incluye al creador si corresponde a un usuario REAL con autoridad:
  // los contratos antiguos traen `createdBy: "system"`, que no resuelve a nadie,
  // y avisarle a esa dirección sería mandar el aviso al vacío.
  if (!cm.createdBy) return [];
  const creador = usuarios.find((u) => u.email === cm.createdBy);
  if (!creador || !esAutoridad(creador)) return [];
  const backs = creador.disciplina
    ? usuarios
        .filter((u) => esAutoridad(u) && u.disciplina === creador.disciplina)
        .map((u) => u.email)
    : [];
  return [...new Set([cm.createdBy, ...backs])];
}
